/* eslint-disable no-shadow */
/** @jsxImportSource @emotion/react */
import { useMutation } from '@apollo/client';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Text from '@Text';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  GRANT_EMAIL_MARKETING_CONSENT,
  GRANT_SMS_MARKETING_CONSENT,
  GRANT_OTT_MARKETING_CONSENT,
  REVOKE_EMAIL_MARKETING_CONSENT,
  REVOKE_SMS_MARKETING_CONSENT,
  REVOKE_OTT_MARKETING_CONSENT,
  areConsentOptionsRequired,
} from './consent.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

export const THANK_YOU_PAGE_LOCATION = 'on_thankyou_page';

const Consent = () => {
  const legalStatement = useSelector((store) => store.legalStatement);
  const submissionId = useSelector((store) => store.submission.id);
  const { formId } = useSelector((store) => store.formConfig);
  const testMode = useSelector((store) => store.optionalFormConfig?.testMode);
  const { marketingConsent } = useSelector((store) => store.formConfig);
  const { email, sms, ott } = marketingConsent;
  const theme = useTheme();
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);
  const [isSmsSubmitted, setIsSmsSubmitted] = useState(false);
  const [isOttSubmitted, setIsOttSubmitted] = useState(false);

  const textExperimentName = useGetExperimentName({
    componentName: 'Text',
  });

  const showMarketingConsentOptions = areConsentOptionsRequired({
    isSmsMarketingConsentRequired: sms.isMarketingConsentRequired,
    isEmailMarketingConsentRequired: email.isMarketingConsentRequired,
    isOttMarketingConsentRequired: ott.isMarketingConsentRequired,
    consentedSuppliersHash: legalStatement.consentedSuppliersHash,
  });

  const [revokeSMSMarketingConsent] = useMutation(REVOKE_SMS_MARKETING_CONSENT);
  const [grantSMSMarketingConsent] = useMutation(GRANT_SMS_MARKETING_CONSENT);
  const [revokeEmailMarketingConsent] = useMutation(
    REVOKE_EMAIL_MARKETING_CONSENT
  );
  const [grantEmailMarketingConsent] = useMutation(
    GRANT_EMAIL_MARKETING_CONSENT
  );
  const [revokeOttMarketingConsent] = useMutation(REVOKE_OTT_MARKETING_CONSENT);
  const [grantOttMarketingConsent] = useMutation(GRANT_OTT_MARKETING_CONSENT);

  const emailMarketingMessageText = (
    <Text variant="text" align="left" experimentName={textExperimentName}>
      {email.mainText}
    </Text>
  );
  const smsMarketingMessageText = (
    <Text variant="text" align="left" experimentName={textExperimentName}>
      {sms.mainText}
    </Text>
  );
  const ottMarketingMessageText = (
    <Text variant="text" align="left" experimentName={textExperimentName}>
      {ott.mainText}
    </Text>
  );

  const handleSmsMarketingConsentClick = async ({
    isOptOut,
    formId,
    submissionId,
  }) => {
    if (isOptOut) {
      await revokeSMSMarketingConsent({
        variables: { submissionId, formId, consentType: 'sms', testMode },
      });
    } else {
      await grantSMSMarketingConsent({
        variables: { submissionId, formId, consentType: 'sms', testMode },
      });
    }
    setIsSmsSubmitted(true);
  };

  const handleEmailMarketingConsentClick = async ({
    isOptOut,
    formId,
    submissionId,
  }) => {
    if (isOptOut) {
      await revokeEmailMarketingConsent({
        variables: { submissionId, formId, consentType: 'email', testMode },
      });
    } else {
      await grantEmailMarketingConsent({
        variables: { submissionId, formId, consentType: 'email', testMode },
      });
    }
    setIsEmailSubmitted(true);
  };

  const handleOttMarketingConsentClick = async ({
    isOptOut,
    formId,
    submissionId,
  }) => {
    if (isOptOut) {
      await revokeOttMarketingConsent({
        variables: { submissionId, formId, consentType: 'ott', testMode },
      });
    } else {
      await grantOttMarketingConsent({
        variables: { submissionId, formId, consentType: 'ott', testMode },
      });
    }
    setIsOttSubmitted(true);
  };

  if (showMarketingConsentOptions) {
    return (
      <Box marginTop={theme.mvf.spacing.medium} data-testid="consentContainer">
        <FormGroup>
          {sms.isMarketingConsentRequired &&
            sms.location === THANK_YOU_PAGE_LOCATION && (
              <Box>
                {isSmsSubmitted ? (
                  <Text variant="note" experimentName={textExperimentName}>
                    {sms.submittedText}
                  </Text>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={() =>
                          handleSmsMarketingConsentClick({
                            isOptOut: sms.isOptOut,
                            formId,
                            submissionId,
                          })
                        }
                      />
                    }
                    label={smsMarketingMessageText}
                  />
                )}
              </Box>
            )}
          {email.isMarketingConsentRequired &&
            email.location === THANK_YOU_PAGE_LOCATION && (
              <Box marginTop={theme.mvf.spacing.small}>
                {isEmailSubmitted ? (
                  <Text
                    marginTop={theme.mvf.spacing.small}
                    variant="note"
                    experimentName={textExperimentName}
                  >
                    {email.submittedText}
                  </Text>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={() =>
                          handleEmailMarketingConsentClick({
                            isOptOut: email.isOptOut,
                            formId,
                            submissionId,
                          })
                        }
                      />
                    }
                    label={emailMarketingMessageText}
                  />
                )}
              </Box>
            )}
          {ott.isMarketingConsentRequired &&
            ott.location === THANK_YOU_PAGE_LOCATION && (
              <Box marginTop={theme.mvf.spacing.small}>
                {isOttSubmitted ? (
                  <Text variant="note" experimentName={textExperimentName}>
                    {ott.submittedText}
                  </Text>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={() =>
                          handleOttMarketingConsentClick({
                            isOptOut: ott.isOptOut,
                            formId,
                            submissionId,
                          })
                        }
                      />
                    }
                    label={ottMarketingMessageText}
                  />
                )}
              </Box>
            )}
        </FormGroup>
      </Box>
    );
  }
  return null;
};

export default Consent;
